import React from 'react';

/**
 * The one surface every floating piece of the editor shares (Editor Glass Pass plan).
 *
 * The toolbar, the prompt card, the ask chip, the menus and the records sheet all sit over the
 * founder's prototype. Given the same frosted pane - translucent, blurred, hairline-edged - they
 * read as one instrument laid over the design rather than four boxes from four places, and the
 * prototype's own colours show through, so the editor looks like it belongs to whatever is
 * underneath.
 *
 * Plain CSS in one `<style>` tag, mounted once: the blur, the fallback where blur is unsupported,
 * the three motions and the reduced-motion switch are things utility classes cannot say in one
 * place. Everything else about these pieces stays in Tailwind.
 */
export const GLASS = 'ideoz-glass';
/** Appear: fade, 6px rise, 98% scale, 160ms. */
export const RISE = 'ideoz-rise';
/** Surface: fade, 8px rise, 220ms - the toolbar on first selection, the sheet. */
export const SURFACE = 'ideoz-surface';
/** A tooltip from `data-tip`, under the element, after a short delay. */
export const TIP = 'ideoz-tip';
/** The 600ms shimmer a picture placeholder wears while it draws. */
export const SHIMMER = 'ideoz-shimmer';

const CSS = `
:root {
  --editor-glass: rgba(255, 255, 255, 0.95);
  --editor-glass-solid: rgba(255, 255, 255, 0.95);
  --editor-edge: rgba(255, 255, 255, 0.75);
  --editor-shadow: 0 10px 34px rgba(20, 24, 40, 0.16), 0 1px 2px rgba(20, 24, 40, 0.08);
  --editor-ink: #16181d;
  --editor-ink-2: rgba(22, 24, 29, 0.62);
  /* Ideoz primary-500 / -100, from ui/tailwind.config.js */
  --editor-accent: #4e5ba6;
  --editor-accent-soft: rgba(78, 91, 166, 0.12);
  --editor-ease: cubic-bezier(0.2, 0.8, 0.2, 1);
}
.dark {
  --editor-glass: rgba(24, 26, 34, 0.90);
  --editor-glass-solid: rgba(24, 26, 34, 0.94);
  --editor-edge: rgba(255, 255, 255, 0.12);
  --editor-shadow: 0 10px 34px rgba(0, 0, 0, 0.5), 0 1px 2px rgba(0, 0, 0, 0.4);
  --editor-ink: #f1f2f7;
  --editor-ink-2: rgba(241, 242, 247, 0.62);
  /* Ideoz primary-300 on dark */
  --editor-accent: #afb5d9;
  --editor-accent-soft: rgba(175, 181, 217, 0.18);
}
.${GLASS} {
  background: var(--editor-glass);
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  backdrop-filter: blur(18px) saturate(170%);
  box-shadow: var(--editor-shadow), inset 0 0 0 1px var(--editor-edge);
  color: var(--editor-ink);
}
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .${GLASS} { background: var(--editor-glass-solid); }
}
.${TIP} { position: relative; }
.${TIP}::after {
  content: attr(data-tip);
  position: absolute;
  left: 50%;
  top: calc(100% + 6px);
  transform: translateX(-50%) translateY(-2px);
  padding: 3px 7px;
  border-radius: 7px;
  background: var(--editor-ink);
  color: var(--editor-glass-solid);
  font: 500 11px/1.3 system-ui, sans-serif;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 120ms var(--editor-ease), transform 120ms var(--editor-ease);
  transition-delay: 0ms;
  z-index: 5;
}
.${TIP}:hover::after, .${TIP}:focus-visible::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  transition-delay: 120ms;
}
@keyframes ideoz-rise {
  from { opacity: 0; transform: translateY(6px) scale(0.98); }
  to { opacity: 1; transform: none; }
}
@keyframes ideoz-surface {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
}
@keyframes ideoz-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
.${RISE} { animation: ideoz-rise 160ms var(--editor-ease) both; }
.${SURFACE} { animation: ideoz-surface 220ms var(--editor-ease) both; }
.${SHIMMER} {
  background: linear-gradient(90deg, var(--editor-accent-soft) 0%, transparent 50%, var(--editor-accent-soft) 100%);
  background-size: 200% 100%;
  animation: ideoz-shimmer 600ms linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .${RISE}, .${SURFACE}, .${SHIMMER} { animation: none; }
  .${TIP}::after { transition: none; }
}
@media (pointer: coarse) {
  [data-ideoz-icon-button] { width: 34px; height: 34px; }
}
`;

let mounted = 0;

/** Mount once near the editor; the styles stay for as long as any copy is mounted. */
export function EditorSurfaceStyles(): React.ReactElement | null {
  React.useEffect(() => {
    mounted += 1;
    let tag = document.getElementById('ideoz-editor-surface') as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = 'ideoz-editor-surface';
      tag.textContent = CSS;
      document.head.appendChild(tag);
    }
    return () => {
      mounted -= 1;
      if (mounted === 0) tag?.remove();
    };
  }, []);
  return null;
}
